import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
} from '@angular/core';
import { IOption } from '@cmps/ng-select';
import { TranslateService } from '@ngx-translate/core';

import { TranslationKeyMap } from '../../coaching-insight.i18n';
import { prependEmptyOption } from '../models/coaching.global.model';
import {
    Evaluation,
    Interaction,
    ObservationHistory,
} from '../models/oa.history.model';
import { SortDirection, SortingField } from '../models/sorting.model';
import { CoachingInsightWidgetService } from '../services/coaching-insight.widget.service';
import { CoachingService } from '../services/coaching.service';

import { AbstractOAHistory } from './oa-history-page.component';

@Component({
    selector: 'cmps-observations-history',
    templateUrl: './observations-history.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObservationsHistoryComponent
    extends AbstractOAHistory<ObservationHistory>
    implements OnInit
{
    evaluations: Evaluation[];
    interactions: Interaction[];

    constructor(
        protected _coachInsightService: CoachingInsightWidgetService,
        protected _coachingService: CoachingService,
        protected cd: ChangeDetectorRef,
        protected _translateService: TranslateService,
    ) {
        super(_coachInsightService, _coachingService, cd, _translateService);
    }

    displayEvaluations = false;

    allRecordsHidden = false;
    hasPendingData = false;
    failLoad = '';

    i18n = TranslationKeyMap;

    defaultSortIndex: number = 0; // sessionDate;

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        //
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    initData() {
        this.hasPendingData = true;

        this.sortFields = [
            new SortingField('sessionDate', 'date', SortDirection.DESC),
            new SortingField('sessionTypeName', 'string'),
            new SortingField('observerName', 'string'),
            new SortingField('statusName', 'string'),
            new SortingField('commitmentCount', 'number'),
        ];

        const x = this._coachingService.retrieveObservationHistory(
            this.performerId,
            this._coachInsightService.getPeriodTypeId(),
            this.numRecords,
        );
        this.sub = x.subscribe(
            (s) => {
                if (s) {
                    this.data = s.history;
                    this.evaluations = s.evaluations;
                    this.interactions = s.interactions;
                    let observers = s.observers;
                    let sessiontypes = s.sessiontypes;
                    const statuses = [];
                    const focuses = [];

                    if (this.data) {
                        this.data.forEach((d: ObservationHistory) => {
                            d.rowSpan = 1;
                            d.display = true;

                            let interactionSpan = 0; // for observation rows
                            let evaluationSpan = 0;

                            let interactions = this.interactions.filter(
                                (i: Interaction) =>
                                    i.coachingEventId === d.coachingEventId,
                            );
                            d.focusId = [];

                            if (interactions.length) {
                                interactions = interactions.map(
                                    (f: Interaction) => {
                                        f.rowSpan = 1;
                                        f.subRowSpan = 1;

                                        interactionSpan++; // for observation rows

                                        let evals = this.evaluations.filter(
                                            (e: Evaluation) =>
                                                e.interactionId ===
                                                    f.interactionId &&
                                                e.behaviourId != null,
                                        );

                                        if (evals.length) {
                                            evals = evals.map(
                                                (x: Evaluation) => {
                                                    if (
                                                        x.behaviour ||
                                                        x.subBehaviour
                                                    ) {
                                                        const b =
                                                            x.behaviour ||
                                                            this._translateService.instant(
                                                                TranslationKeyMap._842_N_A,
                                                            );
                                                        const s =
                                                            x.subBehaviour ||
                                                            this._translateService.instant(
                                                                TranslationKeyMap._842_N_A,
                                                            );
                                                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                                        x.focus = `${b} - ${s}`;
                                                    }
                                                    return x;
                                                },
                                            );

                                            f.evaluations = evals;
                                            f.subRowSpan =
                                                evals.length > 1
                                                    ? evals.length
                                                    : 1;

                                            f.evaluations.forEach(
                                                (e: Evaluation) => {
                                                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                                                    if (
                                                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                                                        d.focusId.indexOf(
                                                            e.focus,
                                                        ) === -1
                                                    ) {
                                                        d.focusId.push(e.focus);
                                                    }
                                                },
                                            );

                                            f.evaluations.forEach(
                                                (e: Evaluation) => {
                                                    const ffound = focuses.find(
                                                        (d) =>
                                                            d.value === e.focus,
                                                    );
                                                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                                    const opt: IOption = <
                                                        IOption
                                                    >{};
                                                    if (!ffound) {
                                                        opt.label = e.focus;
                                                        opt.value = e.focus; // e.interactionId.toString();
                                                        focuses.push(opt);
                                                    }
                                                    evaluationSpan++; // for observation rows
                                                },
                                            );
                                        } else {
                                            f.evaluations = undefined;
                                            evaluationSpan++;
                                        }
                                        return f;
                                    },
                                );

                                // interactions = interactions.filter(h=>h.evaluations && h.evaluations.length);
                            } else {
                                interactionSpan = 1;
                                evaluationSpan = 1;
                            }

                            d.interactions = interactions;
                            d.rowSpan = interactionSpan || 1;
                            d.subRowSpan =
                                interactionSpan > evaluationSpan
                                    ? interactionSpan
                                    : !evaluationSpan
                                    ? 1
                                    : evaluationSpan;
                        });

                        this.data.forEach((f) => {
                            const statusIdStr = f.statusId.toString();
                            const found = statuses.find(
                                (d) => d.value === statusIdStr,
                            );
                            if (!found) {
                                const opt: IOption = {
                                    label: f.statusName,
                                    value: statusIdStr,
                                };
                                statuses.push(opt);
                            }
                        });
                    }

                    if (this.filterOptions) {
                        sessiontypes = sessiontypes.map((d) => {
                            if (d.sessionTypeId) {
                                d.value = d.sessionTypeId.toString();
                            }
                            if (d.sessionTypeName) {
                                d.label = d.sessionTypeName;
                            }
                            return d;
                        });
                        observers = observers.map((d) => {
                            if (d.observerId) {
                                d.value = d.observerId.toString();
                            }
                            if (d.observerName) {
                                d.label = d.observerName;
                            }
                            return d;
                        });

                        this.filterOptions.SessionTypes = sessiontypes;
                        this.filterOptions.Observers = observers;
                        this.filterOptions.Statuses = statuses;
                        this.filterOptions.Focuses = focuses;

                        prependEmptyOption(
                            this.filterOptions.SessionTypes,
                            this.placeholderText,
                        );
                        prependEmptyOption(
                            this.filterOptions.Observers,
                            this.placeholderText,
                        );
                        prependEmptyOption(
                            this.filterOptions.Statuses,
                            this.placeholderText,
                        );
                        prependEmptyOption(
                            this.filterOptions.Focuses,
                            this.placeholderText,
                        );
                    }
                    this.sortByFieldDefault();
                    this.getDisplayable();
                } else {
                    this.failLoad = this._translateService.instant(
                        TranslationKeyMap._7447_FAILED_TO_LOAD_DATA,
                    );
                }
                this.emitDisplayStatus();
                this.hasPendingData = false;
                this.cd.detectChanges();
            },
            (e) => {
                this.failLoad = this._translateService.instant(
                    TranslationKeyMap._7447_FAILED_TO_LOAD_DATA,
                );
                this.hasPendingData = false;
                this.cd.detectChanges();
            },
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    showSubs() {
        this.displayEvaluations = true;
        this.data.forEach((o) => {
            if (o.interactions) {
                o.rowSpan = o.interactions.length || 1;
                o.interactions.forEach((h) => {
                    if (h.evaluations) {
                        h.rowSpan = h.evaluations.length;
                    }
                });
            }
        });
        this.cd.detectChanges();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    hideSubs() {
        this.displayEvaluations = false;
        this.data.forEach((o) => {
            o.rowSpan = o.interactions.length || 1;
            o.interactions.forEach((f) => {
                f.rowSpan = 1;
            });
        });
        this.cd.detectChanges();
    }
}
